var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import classnames from 'classnames';
import memoize from 'fast-memoize';
import * as React from 'react';
import { fromEvent, merge, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import './index.css';
import { Resizer } from './resizer';
import hasDirection from './utils/hasDirection';
import isMouseEvent from './utils/isMouseEvent';
import isTouchEvent from './utils/isTouchEvent';
import { cancelAnimationFrame, requestAnimationFrame } from './utils/requestAnimationFrame';
var prefixClass = 'react-resizable';
var previewClass = prefixClass + "-preview";
var handleWrapperClass = prefixClass + "-wrapper";
var DEFAULT_SIZE = {
    width: 'auto',
    height: 'auto',
};
var definedProps = [
    'style',
    'className',
    'bounds',
    'boundsByDirection',
    'width',
    'defaultWidth',
    'height',
    'defaultHeight',
    'directions',
    'maxHeight',
    'maxWidth',
    'minHeight',
    'minWidth',
    'resizeRatio',
    'scale',
    'lockAspectRatio',
    'lockAspectRatioExtraWidth',
    'lockAspectRatioExtraHeight',
    'preview',
    'previewStyle',
    'previewClass',
    'disabled',
    'handleComponent',
    'onResizeStart',
    'onResize',
    'onResizeEnd',
];
// HACK: This class is used to calculate % size.
var baseClassName = '__resizable_base__';
var Resizable = /** @class */ (function (_super) {
    __extends(Resizable, _super);
    function Resizable(props) {
        var _this = _super.call(this, props) || this;
        _this.resizable = null;
        _this.previewElement = null;
        _this.destroyed$ = new Subject();
        _this.resize$ = new Subject();
        _this.ratio = 1;
        _this.parentWidth = 0;
        _this.parentHeight = 0;
        // For parent boundary
        _this.parentLeft = 0;
        _this.parentTop = 0;
        // For boundary
        _this.resizableLeft = 0;
        _this.resizableRight = 0;
        _this.resizableTop = 0;
        _this.resizableBottom = 0;
        // For target boundary
        _this.targetLeft = 0;
        _this.targetTop = 0;
        _this.appendBase = function () {
            if (!_this.resizable || !_this.window) {
                return null;
            }
            var parent = _this.parentNode;
            if (!parent) {
                return null;
            }
            var element = _this.window.document.createElement('div');
            element.style.width = '100%';
            element.style.height = '100%';
            element.style.position = 'absolute';
            element.style.transform = 'scale(0, 0)';
            element.style.left = '0';
            element.style.flex = '0';
            if (element.classList) {
                element.classList.add(baseClassName);
            }
            else {
                element.className += baseClassName;
            }
            parent.appendChild(element);
            return element;
        };
        _this.removeBase = function (base) {
            var parent = _this.parentNode;
            if (!parent) {
                return;
            }
            parent.removeChild(base);
        };
        _this.getStringSize = function (width, height) {
            var newWidth = width;
            var newHeight = height;
            var _a = _this.state, originalWidth = _a.width, originalHeight = _a.height;
            if (originalWidth && typeof originalWidth === 'string') {
                if (endsWith(originalWidth, '%')) {
                    var percent = (newWidth / _this.parentWidth) * 100;
                    newWidth = percent + "%";
                }
                else if (endsWith(originalWidth, 'vw')) {
                    var vw = (newWidth / _this.window.innerWidth) * 100;
                    newWidth = vw + "vw";
                }
                else if (endsWith(originalWidth, 'vh')) {
                    var vh = (newWidth / _this.window.innerHeight) * 100;
                    newWidth = vh + "vh";
                }
            }
            if (originalHeight && typeof originalHeight === 'string') {
                if (endsWith(originalHeight, '%')) {
                    var percent = (newHeight / _this.parentHeight) * 100;
                    newHeight = percent + "%";
                }
                else if (endsWith(originalHeight, 'vw')) {
                    var vw = (newHeight / _this.window.innerWidth) * 100;
                    newHeight = vw + "vw";
                }
                else if (endsWith(originalHeight, 'vh')) {
                    var vh = (newHeight / _this.window.innerHeight) * 100;
                    newHeight = vh + "vh";
                }
            }
            return [newWidth, newHeight];
        };
        _this.getSize = function (direction, startPosition, endPosition, originalSize) {
            var _a = _this.props, maxWidth = _a.maxWidth, maxHeight = _a.maxHeight, minWidth = _a.minWidth, minHeight = _a.minHeight;
            var max = calculateNewMax({ width: _this.parentWidth, height: _this.parentHeight }, _this.window.innerWidth, _this.window.innerHeight, maxWidth, maxHeight, minWidth, minHeight);
            maxWidth = max.maxWidth;
            maxHeight = max.maxHeight;
            minWidth = max.minWidth;
            minHeight = max.minHeight;
            var _b = _this.calculateNewSizeFromDirection(direction, startPosition, endPosition, originalSize), newWidth = _b[0], newHeight = _b[1];
            var boundaryMax = _this.calculateNewMaxFromBoundary(direction, maxWidth, maxHeight);
            var newSize = _this.calculateNewSizeFromAspectRatio(newWidth, newHeight, { width: boundaryMax.maxWidth, height: boundaryMax.maxHeight }, { width: minWidth, height: minHeight });
            newWidth = newSize.newWidth;
            newHeight = newSize.newHeight;
            return [newWidth, newHeight];
        };
        _this.calculateNewSizeFromDirection = function (direction, startPosition, endPosition, originalSize) {
            var scale = _this.props.scale || 1;
            var resizeRatio = _this.props.resizeRatio || 1;
            var _a = _this.props, lockAspectRatio = _a.lockAspectRatio, _b = _a.lockAspectRatioExtraHeight, extraWidth = _b === void 0 ? 0 : _b, _c = _a.lockAspectRatioExtraWidth, extraHeight = _c === void 0 ? 0 : _c;
            var startClientX = startPosition[0], startClientY = startPosition[1];
            var endClientX = endPosition[0], endClientY = endPosition[1];
            var originalWidth = originalSize[0], originalHeight = originalSize[1];
            var ratio = originalWidth / originalHeight;
            var newWidth = originalWidth;
            var newHeight = originalHeight;
            if (hasDirection('right', direction)) {
                newWidth = originalWidth + ((endClientX - startClientX) * resizeRatio) / scale;
                if (lockAspectRatio) {
                    newHeight = (newWidth - extraWidth) / ratio + extraHeight;
                }
            }
            if (hasDirection('left', direction)) {
                newWidth = originalWidth - ((endClientX - startClientX) * resizeRatio) / scale;
                if (lockAspectRatio) {
                    newHeight = (newWidth - extraWidth) / ratio + extraHeight;
                }
            }
            if (hasDirection('bottom', direction)) {
                newHeight = originalHeight + ((endClientY - startClientY) * resizeRatio) / scale;
                if (lockAspectRatio) {
                    newWidth = (newHeight - extraHeight) * ratio + extraWidth;
                }
            }
            if (hasDirection('top', direction)) {
                newHeight = originalHeight - ((endClientY - startClientY) * resizeRatio) / scale;
                if (lockAspectRatio) {
                    newWidth = (newHeight - extraHeight) * ratio + extraWidth;
                }
            }
            return [newWidth, newHeight];
        };
        _this.startResize = function (width, height) {
            _this.setState({ isResizing: true });
            _this.setBoundingClientRect();
            // For lockAspectRatio case
            _this.ratio = typeof _this.props.lockAspectRatio === 'number' ? _this.props.lockAspectRatio : width / height;
            var parentSize = _this.getParentSize();
            _this.parentWidth = parentSize.width;
            _this.parentHeight = parentSize.height;
            var parent = _this.parentNode;
            if (parent) {
                var dir = _this.window.getComputedStyle(parent).flexDirection;
                _this.flexDir = dir.startsWith('row') ? 'row' : 'column';
            }
        };
        _this.endResize = function (e, direction) {
            var callback = function () {
                var onResizeEnd = _this.props.onResizeEnd;
                if (onResizeEnd) {
                    onResizeEnd({
                        width: _this.state.width,
                        height: _this.state.height,
                        event: e,
                        direction: direction,
                    });
                }
            };
            _this.setState({ isResizing: false });
            document.body.style.cursor = _this.bakCusor;
            if (_this.sizeCache) {
                _this.setSizeByDirection(__assign(__assign({}, _this.sizeCache), { direction: direction }), function () {
                    _this.sizeCache = undefined;
                    callback();
                });
            }
            else {
                callback();
            }
        };
        _this.onResizeStart = function (event, direction) {
            if (_this.props.disabled) {
                return;
            }
            if (_this.props.onResizeStart) {
                if (_this.resizable) {
                    var _a = _this.state, width = _a.width, height = _a.height;
                    var startResize = _this.props.onResizeStart({
                        event: event,
                        direction: direction,
                        width: width,
                        height: height,
                    });
                    if (startResize === false) {
                        return;
                    }
                }
            }
            _this.resize$.next({ event: event, direction: direction });
        };
        _this.ref = function (c) {
            if (c) {
                _this.resizable = c;
            }
        };
        _this.previewRef = function (c) {
            if (c) {
                _this.previewElement = c;
            }
        };
        _this.state = {
            isResizing: false,
            width: _this.propsSize.width,
            height: _this.propsSize.height,
        };
        return _this;
    }
    Object.defineProperty(Resizable.prototype, "parentNode", {
        get: function () {
            if (!this.resizable) {
                return null;
            }
            return this.resizable.parentNode;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Resizable.prototype, "window", {
        get: function () {
            if (!this.resizable) {
                return null;
            }
            if (!this.resizable.ownerDocument) {
                return null;
            }
            return this.resizable.ownerDocument.defaultView;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Resizable.prototype, "propsSize", {
        get: function () {
            var width = this.props.width || this.props.defaultWidth || DEFAULT_SIZE.width;
            var height = this.props.height || this.props.defaultHeight || DEFAULT_SIZE.height;
            return { width: width, height: height };
        },
        enumerable: false,
        configurable: true
    });
    Resizable.prototype.componentDidMount = function () {
        this.handleResize();
    };
    Resizable.prototype.componentWillUnmount = function () {
        this.destroyed$.next();
        this.destroyed$.complete();
    };
    Resizable.prototype.getParentSize = function () {
        if (!this.parentNode) {
            if (!this.window) {
                return { width: 0, height: 0 };
            }
            return { width: this.window.innerWidth, height: this.window.innerHeight };
        }
        var base = this.appendBase();
        if (!base) {
            return { width: 0, height: 0 };
        }
        // INFO: To calculate parent width with flex layout
        var wrapChanged = false;
        var wrap = this.parentNode.style.flexWrap;
        if (wrap !== 'wrap') {
            wrapChanged = true;
            this.parentNode.style.flexWrap = 'wrap';
            // HACK: Use relative to get parent padding size
        }
        base.style.position = 'relative';
        base.style.minWidth = '100%';
        var size = {
            width: base.offsetWidth,
            height: base.offsetHeight,
        };
        if (wrapChanged) {
            this.parentNode.style.flexWrap = wrap;
        }
        this.removeBase(base);
        return size;
    };
    Resizable.prototype.setBoundingClientRect = function () {
        // For parent boundary
        if (this.props.bounds === 'parent') {
            var parent_1 = this.parentNode;
            if (parent_1) {
                var parentRect = parent_1.getBoundingClientRect();
                this.parentLeft = parentRect.left;
                this.parentTop = parentRect.top;
            }
        }
        // For target(html element) boundary
        if (this.props.bounds && typeof this.props.bounds !== 'string') {
            var targetRect = this.props.bounds.getBoundingClientRect();
            this.targetLeft = targetRect.left;
            this.targetTop = targetRect.top;
        }
        // For boundary
        if (this.resizable) {
            var _a = this.resizable.getBoundingClientRect(), left = _a.left, top_1 = _a.top, right = _a.right, bottom = _a.bottom;
            this.resizableLeft = left;
            this.resizableRight = right;
            this.resizableTop = top_1;
            this.resizableBottom = bottom;
        }
    };
    Resizable.prototype.handleResize = function () {
        var _this = this;
        this.resize$
            .pipe(tap(function (_a) {
            var direction = _a.direction, event = _a.event;
            if (!_this.resizable) {
                return;
            }
            var _b = _this.resizable, clientWidth = _b.clientWidth, clientHeight = _b.clientHeight;
            _this.startResize(clientWidth, clientHeight);
            var startClientX = 0;
            var startClientY = 0;
            if (event.nativeEvent && isMouseEvent(event.nativeEvent)) {
                startClientX = event.nativeEvent.clientX;
                startClientY = event.nativeEvent.clientY;
                // When user click with right button the resize is stuck in resizing mode
                // until users clicks again, dont continue if right click is used.
                // HACK: MouseEvent does not have `which` from flow-bin v0.68.
                if (event.nativeEvent.which === 3) {
                    return;
                }
            }
            else if (event.nativeEvent && isTouchEvent(event.nativeEvent)) {
                startClientX = event.nativeEvent.touches[0].clientX;
                startClientY = event.nativeEvent.touches[0].clientY;
            }
            _this.bakCusor = document.body.style.cursor;
            merge(fromEvent(window, 'mousemove'), fromEvent(window, 'touchmove'))
                .pipe(takeUntil(merge(fromEvent(window, 'mouseup'), fromEvent(window, 'touchend')).pipe(tap(function (e) { return _this.endResize(e, direction); }))))
                .subscribe(function (e) {
                var moveEvent = e;
                if (window.TouchEvent && isTouchEvent(moveEvent)) {
                    try {
                        moveEvent.preventDefault();
                        moveEvent.stopPropagation();
                    }
                    catch (e) {
                        // Ignore on fail
                    }
                }
                var endClientX = isTouchEvent(moveEvent) ? moveEvent.touches[0].clientX : moveEvent.clientX;
                var endClientY = isTouchEvent(moveEvent) ? moveEvent.touches[0].clientY : moveEvent.clientY;
                cancelAnimationFrame(_this.animationTimer);
                _this.animationTimer = requestAnimationFrame(function () {
                    if (_this.props.disabled) {
                        _this.endResize(e, direction);
                        return;
                    }
                    var _a = _this.getSize(direction, [startClientX, startClientY], [endClientX, endClientY], [clientWidth, clientHeight]), widthNum = _a[0], heightNum = _a[1];
                    var _b = _this.getStringSize(widthNum, heightNum), width = _b[0], height = _b[1];
                    if (_this.props.preview && _this.previewElement) {
                        _this.sizeCache = { width: width, height: height };
                        _this.previewResize(widthNum, heightNum);
                    }
                    else if (!_this.props.preview) {
                        var onResize = _this.props.onResize;
                        _this.setSizeByDirection({ direction: direction, width: width, height: height });
                        if (onResize) {
                            onResize({
                                width: width,
                                height: height,
                                direction: direction,
                                event: e,
                            });
                        }
                    }
                });
            });
        }))
            .pipe(takeUntil(this.destroyed$))
            .subscribe();
    };
    Resizable.prototype.setSizeByDirection = function (_a, callback) {
        var direction = _a.direction, width = _a.width, height = _a.height;
        var newState = {};
        if (hasDirection('left', direction) || hasDirection('right', direction)) {
            newState.width = width;
        }
        if (hasDirection('top', direction) || hasDirection('bottom', direction)) {
            newState.height = height;
        }
        this.setState(__assign({}, newState), function () {
            callback && callback();
        });
    };
    Resizable.prototype.previewResize = function (width, height) {
        this.previewElement.style.width = width + "px";
        this.previewElement.style.height = height + "px";
    };
    Resizable.prototype.calculateNewSizeFromAspectRatio = function (newWidth, newHeight, max, min) {
        var _a = this.props, lockAspectRatio = _a.lockAspectRatio, lockAspectRatioExtraHeight = _a.lockAspectRatioExtraHeight, lockAspectRatioExtraWidth = _a.lockAspectRatioExtraWidth;
        var computedMinWidth = typeof min.width === 'undefined' ? 0 : min.width;
        var computedMaxWidth = typeof max.width === 'undefined' || max.width < 0 ? newWidth : max.width;
        var computedMinHeight = typeof min.height === 'undefined' ? 0 : min.height;
        var computedMaxHeight = typeof max.height === 'undefined' || max.height < 0 ? newHeight : max.height;
        var extraHeight = lockAspectRatioExtraHeight || 0;
        var extraWidth = lockAspectRatioExtraWidth || 0;
        if (lockAspectRatio) {
            var extraMinWidth = (computedMinHeight - extraHeight) * this.ratio + extraWidth;
            var extraMaxWidth = (computedMaxHeight - extraHeight) * this.ratio + extraWidth;
            var extraMinHeight = (computedMinWidth - extraWidth) / this.ratio + extraHeight;
            var extraMaxHeight = (computedMaxWidth - extraWidth) / this.ratio + extraHeight;
            var lockedMinWidth = Math.max(computedMinWidth, extraMinWidth);
            var lockedMaxWidth = Math.min(computedMaxWidth, extraMaxWidth);
            var lockedMinHeight = Math.max(computedMinHeight, extraMinHeight);
            var lockedMaxHeight = Math.min(computedMaxHeight, extraMaxHeight);
            newWidth = clamp(newWidth, lockedMinWidth, lockedMaxWidth);
            newHeight = clamp(newHeight, lockedMinHeight, lockedMaxHeight);
        }
        else {
            newWidth = clamp(newWidth, computedMinWidth, computedMaxWidth);
            newHeight = clamp(newHeight, computedMinHeight, computedMaxHeight);
        }
        return { newWidth: newWidth, newHeight: newHeight };
    };
    Resizable.prototype.calculateNewMaxFromBoundary = function (direction, maxWidth, maxHeight) {
        var boundsByDirection = this.props.boundsByDirection;
        var widthByDirection = boundsByDirection && hasDirection('left', direction);
        var heightByDirection = boundsByDirection && hasDirection('top', direction);
        var boundWidth;
        var boundHeight;
        if (this.props.bounds === 'parent') {
            var parent_2 = this.parentNode;
            if (parent_2) {
                boundWidth = widthByDirection
                    ? this.resizableRight - this.parentLeft
                    : parent_2.offsetWidth + (this.parentLeft - this.resizableLeft);
                boundHeight = heightByDirection
                    ? this.resizableBottom - this.parentTop
                    : parent_2.offsetHeight + (this.parentTop - this.resizableTop);
            }
        }
        else if (this.props.bounds === 'window') {
            if (this.window) {
                boundWidth = widthByDirection ? this.resizableRight : this.window.innerWidth - this.resizableLeft;
                boundHeight = heightByDirection ? this.resizableBottom : this.window.innerHeight - this.resizableTop;
            }
        }
        else if (this.props.bounds) {
            boundWidth = widthByDirection
                ? this.resizableRight - this.targetLeft
                : this.props.bounds.offsetWidth + (this.targetLeft - this.resizableLeft);
            boundHeight = heightByDirection
                ? this.resizableBottom - this.targetTop
                : this.props.bounds.offsetHeight + (this.targetTop - this.resizableTop);
        }
        if (boundWidth && Number.isFinite(boundWidth)) {
            maxWidth = maxWidth && maxWidth < boundWidth ? maxWidth : boundWidth;
        }
        if (boundHeight && Number.isFinite(boundHeight)) {
            maxHeight = maxHeight && maxHeight < boundHeight ? maxHeight : boundHeight;
        }
        return { maxWidth: maxWidth, maxHeight: maxHeight };
    };
    Resizable.prototype.renderResizer = function () {
        var _this = this;
        var _a = this.props, directions = _a.directions, disabled = _a.disabled, handleComponent = _a.handleComponent;
        if (!directions) {
            return null;
        }
        var resizers = Object.keys(directions).map(function (dir) {
            if (directions[dir] !== false) {
                return (React.createElement(Resizer, { key: dir, direction: dir, disabled: disabled, onResizeStart: _this.onResizeStart }, handleComponent && handleComponent[dir] ? handleComponent[dir] : null));
            }
            return null;
        });
        // #93 Wrap the resize box in span (will not break 100% width/height)
        return React.createElement("div", { className: handleWrapperClass }, resizers);
    };
    Resizable.prototype.render = function () {
        var _this = this;
        var extendsProps = Object.keys(this.props).reduce(function (acc, key) {
            if (definedProps.indexOf(key) !== -1) {
                return acc;
            }
            acc[key] = _this.props[key];
            return acc;
        }, {});
        var style = __assign(__assign({ position: 'relative', userSelect: this.state.isResizing ? 'none' : 'auto' }, this.props.style), { maxWidth: this.props.maxWidth, maxHeight: this.props.maxHeight, minWidth: this.props.minWidth, minHeight: this.props.minHeight, boxSizing: 'border-box', flexShrink: 0, width: this.props.width || this.state.width, height: this.props.height || this.state.height });
        if (this.flexDir) {
            style.flexBasis = this.flexDir === 'row' ? style.width : style.height;
        }
        return (React.createElement("div", __assign({}, extendsProps, { ref: this.ref, style: style, className: this.props.className }),
            this.state.isResizing && React.createElement("div", null),
            this.props.children,
            this.renderResizer(),
            this.props.preview && this.state.isResizing && (React.createElement("div", { ref: this.previewRef, className: classnames(this.props.previewClass, previewClass), style: __assign({}, (this.props.previewStyle || {})) }))));
    };
    Resizable.defaultProps = {
        onResizeStart: function () { },
        onResize: function () { },
        onResizeEnd: function () { },
        bounds: 'parent',
        boundsByDirection: true,
        directions: {
            top: true,
            right: true,
            bottom: true,
            left: true,
            topRight: true,
            bottomRight: true,
            bottomLeft: true,
            topLeft: true,
        },
        style: {},
        lockAspectRatio: false,
        disabled: false,
        preview: false,
        scale: 1,
        resizeRatio: 1,
        minWidth: 0,
        minHeight: 0,
    };
    return Resizable;
}(React.PureComponent));
export { Resizable };
var calculateNewMax = memoize(function (parentSize, innerWidth, innerHeight, maxWidth, maxHeight, minWidth, minHeight) {
    maxWidth = getPixelSize(maxWidth, parentSize.width, innerWidth, innerHeight);
    maxHeight = getPixelSize(maxHeight, parentSize.height, innerWidth, innerHeight);
    minWidth = getPixelSize(minWidth, parentSize.width, innerWidth, innerHeight);
    minHeight = getPixelSize(minHeight, parentSize.height, innerWidth, innerHeight);
    return {
        maxWidth: typeof maxWidth === 'undefined' ? undefined : Number(maxWidth),
        maxHeight: typeof maxHeight === 'undefined' ? undefined : Number(maxHeight),
        minWidth: typeof minWidth === 'undefined' ? undefined : Number(minWidth),
        minHeight: typeof minHeight === 'undefined' ? undefined : Number(minHeight),
    };
});
var getPixelSize = function (size, parentSize, innerWidth, innerHeight) {
    if (size && typeof size === 'string') {
        if (endsWith(size, 'px')) {
            return Number(size.replace('px', ''));
        }
        if (endsWith(size, '%')) {
            var ratio = Number(size.replace('%', '')) / 100;
            return parentSize * ratio;
        }
        if (endsWith(size, 'vw')) {
            var ratio = Number(size.replace('vw', '')) / 100;
            return innerWidth * ratio;
        }
        if (endsWith(size, 'vh')) {
            var ratio = Number(size.replace('vh', '')) / 100;
            return innerHeight * ratio;
        }
    }
    return size;
};
var endsWith = memoize(function (str, searchStr) {
    return str.substr(str.length - searchStr.length, searchStr.length) === searchStr;
});
var clamp = memoize(function (n, min, max) { return Math.max(Math.min(n, max), min); });
